/**
 * Harper spell + grammar engine shared by both editor islands (#250):
 * WASM linter lifecycle (worker, main-thread fallback, timeouts), the project
 * lexicon, and plain-text lint passes. Editor glue lives in `prose/harper.ts`,
 * which registers a refresh callback so dictionary changes re-lint every open
 * editor.
 */

import * as Sentry from "@sentry/vue";
import {
  LocalLinter,
  SuggestionKind,
  WorkerLinter,
  type Lint,
  type Linter,
} from "harper.js";
import { binary } from "harper.js/binary";
import { isNothing, just, nothing, toMaybe, type Maybe } from "../lib/maybe";
import { expandPossessiveNameForms } from "../lib/nameForms";
import {
  addUserDictionaryWord,
  databaseWordsFromNames,
  loadUserDictionary,
} from "../lib/projectDictionary";
import {
  buildFictionLintConfig,
  isFictionLintKind,
} from "./harperFictionConfig";

export type HarperSeverity = "error" | "warning" | "info";

/** A suggested edit, relative to the finding's range. */
export type HarperFix = {
  kind: "replace" | "remove" | "insertAfter";
  text: string;
  /** Menu label ("Remove", "Insert ,", the replacement word). */
  label: string;
};

/** One lint, with offsets into the text that was linted. */
export type HarperFinding = {
  from: number;
  to: number;
  severity: HarperSeverity;
  /** Harper's sentence; "" when there are fixes (they speak for themselves). */
  message: string;
  fixes: HarperFix[];
  /** The misspelt word, when "Add to dictionary" applies. */
  dictionaryWord: Maybe<string>;
};

/** The text change a fix makes to `from..to`. */
export function harperFixEdit(
  fix: HarperFix,
  from: number,
  to: number,
): { from: number; to: number; insert: string } {
  switch (fix.kind) {
    case "remove":
      return { from, to, insert: "" };
    case "insertAfter":
      return { from: to, to, insert: fix.text };
    case "replace":
      return { from, to, insert: fix.text };
  }
}

type Lexicon = {
  rootPath: string | null;
  userWords: string[];
  databaseWords: string[];
  appliedKey: string;
};

const lexicon: Lexicon = {
  rootPath: null,
  userWords: [],
  databaseWords: [],
  appliedKey: "",
};

let linterPromise: Promise<Linter> | null = null;

export type HarperBackend = "worker" | "main-thread";
export type HarperStatus =
  | { state: "idle" }
  | { state: "starting"; backend: HarperBackend }
  | { state: "ready"; backend: HarperBackend }
  | { state: "failed"; message: string };

let status: HarperStatus = { state: "idle" };

/** Current linter state. Surfaced for support/diagnostics. */
export function harperStatus(): HarperStatus {
  return status;
}

/**
 * harper.js never rejects when its worker wedges: `processRequest` has no
 * try/catch, so a failed WASM load inside the worker simply never posts a
 * reply, and every later call queues behind it forever. Without a timeout the
 * editor shows no underlines and logs nothing, which reads as "Harper is
 * broken" with no way to diagnose it.
 *
 * Worker setup uses a short probe so wedged environments (some mobile
 * browsers) fall back to the main thread quickly instead of waiting the full
 * cold-start budget.
 */
const WORKER_PROBE_TIMEOUT_MS = 3_000;
const SETUP_TIMEOUT_MS = 20_000;
const LINT_TIMEOUT_MS = 20_000;

function withTimeout<T>(
  work: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms`)),
      ms,
    );
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/** Expected recovery path (worker → main thread). Console only; not Sentry. */
function warnHarperRecovery(message: string, error: unknown): void {
  console.warn(message, error);
}

function reportHarperProblem(message: string, error: unknown): void {
  console.error(message, error);
  Sentry.captureException(error, {
    tags: { feature: "harper" },
    extra: { message, backend: status },
  });
}

async function applyFictionDefaults(harper: Linter): Promise<void> {
  // Rule keys change across harper.js releases — derive from structured
  // groups rather than hard-coding the full rule list.
  const structured = await harper.getStructuredLintConfig();
  await harper.setLintConfig(buildFictionLintConfig(structured));
}

async function startLinter(backend: HarperBackend): Promise<Linter> {
  status = { state: "starting", backend };
  const instance =
    backend === "worker"
      ? new WorkerLinter({ binary })
      : new LocalLinter({ binary });
  const setupTimeoutMs =
    backend === "worker" ? WORKER_PROBE_TIMEOUT_MS : SETUP_TIMEOUT_MS;
  try {
    await withTimeout(
      instance.setup(),
      setupTimeoutMs,
      `Harper ${backend} setup`,
    );
    await withTimeout(
      applyFictionDefaults(instance),
      SETUP_TIMEOUT_MS,
      `Harper ${backend} config`,
    );
    status = { state: "ready", backend };
    return instance;
  } catch (error) {
    // Tear down so orphaned worker fetches do not reject as unhandled AbortError.
    void instance.dispose().catch(() => {});
    throw error;
  }
}

function cacheLinter(work: Promise<Linter>): Promise<Linter> {
  const cached = work.catch((error) => {
    // Allow a later warmup/sync to retry instead of caching a rejection.
    if (linterPromise === cached) linterPromise = null;
    status = {
      state: "failed",
      message: error instanceof Error ? error.message : String(error),
    };
    throw error;
  });
  linterPromise = cached;
  return cached;
}

async function getHarper(): Promise<Linter> {
  if (linterPromise !== null) return linterPromise;
  return cacheLinter(
    (async () => {
      try {
        // WorkerLinter keeps WASM off the UI thread (Harper's recommendation
        // for interactive editors).
        return await startLinter("worker");
      } catch (error) {
        // Worker startup is environment-sensitive (WebView version, CSP).
        // A slower main-thread linter still beats no spellcheck at all.
        warnHarperRecovery(
          "Harper worker unavailable; falling back to the main thread",
          error,
        );
        return await startLinter("main-thread");
      }
    })(),
  );
}

/** Drop a wedged linter so the next pass rebuilds on the main thread. */
function demoteToMainThread(error: unknown): void {
  const alreadyLocal =
    status.state === "ready" && status.backend === "main-thread";
  if (alreadyLocal) {
    reportHarperProblem("Harper stalled on the main thread", error);
  } else {
    warnHarperRecovery(
      "Harper worker stalled; retrying on the main thread",
      error,
    );
  }
  // Rebuilding a main-thread linter that just timed out would only stall again.
  if (alreadyLocal) return;
  void cacheLinter(startLinter("main-thread")).catch(() => {});
}

/**
 * Handle a rejected lint pass. Timeouts demote the linter and ask the caller
 * to retry (underlines return on the next pass); anything else is reported.
 */
export function recoverFromLintFailure(error: unknown): "retry" | "failed" {
  const timedOut =
    error instanceof Error && error.message.includes("timed out");
  if (timedOut) {
    demoteToMainThread(error);
    return "retry";
  }
  reportHarperProblem("Harper lint failed", error);
  return "failed";
}

function lexiconKey(): string {
  return [
    lexicon.rootPath ?? "",
    ...lexicon.userWords,
    "|",
    ...lexicon.databaseWords,
  ].join("\0");
}

async function applyLexicon(harper: Linter): Promise<void> {
  const key = lexiconKey();
  if (key === lexicon.appliedKey) return;
  await harper.clearWords();
  const words = expandPossessiveNameForms([
    ...lexicon.userWords,
    ...lexicon.databaseWords,
  ]);
  if (words.length > 0) {
    await harper.importWords(words);
  }
  lexicon.appliedKey = key;
}

function fixFor(kind: SuggestionKind, text: string): HarperFix {
  if (kind === SuggestionKind.Remove) {
    return { kind: "remove", text, label: "Remove" };
  }
  if (kind === SuggestionKind.InsertAfter) {
    return {
      kind: "insertAfter",
      text,
      label: text.length > 0 ? `Insert ${text}` : "Insert",
    };
  }
  return { kind: "replace", text, label: text.length > 0 ? text : "Replace" };
}

function severityFor(lint: Lint): HarperSeverity {
  const kind = lint.lint_kind();
  if (kind === "Spelling" || kind === "Typo") return "error";
  if (kind === "Style" || kind === "Readability" || kind === "Enhancement") {
    return "info";
  }
  return "warning";
}

function isDictionaryLint(lint: Lint): boolean {
  const kind = lint.lint_kind();
  return kind === "Spelling" || kind === "Typo";
}

function findingFor(lint: Lint): HarperFinding {
  const span = lint.span();
  const fixes = lint
    .suggestions()
    .slice(0, 3)
    .map((suggestion) =>
      fixFor(suggestion.kind(), suggestion.get_replacement_text()),
    );
  const problem = isDictionaryLint(lint) ? lint.get_problem_text().trim() : "";
  const dictionaryWord = problem === "" ? nothing : just(problem);
  const hasActions = fixes.length > 0 || problem !== "";
  return {
    from: span.start,
    to: span.end,
    severity: severityFor(lint),
    message: hasActions ? "" : lint.message(),
    fixes,
    dictionaryWord,
  };
}

/**
 * Lint `text` as plain prose. Nothing when `isCurrent()` turns false while
 * waiting (a newer pass owns the result). Rejects on failure; pass the error
 * to `recoverFromLintFailure`.
 */
export async function lintPlainText(
  text: string,
  isCurrent: () => boolean,
): Promise<Maybe<HarperFinding[]>> {
  if (text.trim().length === 0) return just([]);

  const harper = await getHarper();
  await withTimeout(applyLexicon(harper), SETUP_TIMEOUT_MS, "Harper lexicon");
  if (isCurrent() === false) return nothing;

  // Plaintext, not markdown. Harper's markdown mode treats a leading tab or
  // 4+ spaces as an indented code block and skips the whole paragraph, which
  // silently kills spellcheck on any indented prose (common in manuscripts).
  const lints = await withTimeout(
    harper.lint(text, { language: "plaintext" }),
    LINT_TIMEOUT_MS,
    "Harper lint",
  );
  const findings: HarperFinding[] = [];
  for (const lint of lints) {
    try {
      // Defense in depth: rule config should already suppress style/curses,
      // but kinds can still slip through after harper.js upgrades.
      if (isCurrent() && isFictionLintKind(lint.lint_kind())) {
        findings.push(findingFor(lint));
      }
    } finally {
      lint.free();
    }
  }
  return isCurrent() ? just(findings) : nothing;
}

// --- Per-block cache -----------------------------------------------------------

const BLOCK_JOIN = "\n\n";
const BLOCK_CACHE_LIMIT = 4_000;
const blockCache = new Map<string, HarperFinding[]>();
let blockCacheKey = "";

/**
 * Lint each block separately (offsets relative to its block), reusing cached
 * results for blocks whose text and the lexicon haven't changed. Uncached
 * blocks go to Harper in one pass. Nothing when superseded (see
 * `lintPlainText`).
 */
export async function lintBlocks(
  blocks: readonly string[],
  isCurrent: () => boolean,
): Promise<Maybe<HarperFinding[][]>> {
  const key = lexiconKey();
  if (key !== blockCacheKey || blockCache.size > BLOCK_CACHE_LIMIT) {
    blockCache.clear();
    blockCacheKey = key;
  }

  const pending = [
    ...new Set(blocks.filter((b) => b.trim() !== "" && !blockCache.has(b))),
  ];
  if (pending.length > 0) {
    const starts: number[] = [];
    let joined = "";
    for (const block of pending) {
      if (joined !== "") joined += BLOCK_JOIN;
      starts.push(joined.length);
      joined += block;
    }
    const linted = await lintPlainText(joined, isCurrent);
    // The dictionary may have changed mid-pass; don't cache under the new key.
    if (isNothing(linted) || lexiconKey() !== key) return nothing;
    pending.forEach((block, i) => {
      const start = starts[i] ?? 0;
      const end = start + block.length;
      blockCache.set(
        block,
        linted.value
          .filter((f) => f.from >= start && f.to <= end)
          .map((f) => ({ ...f, from: f.from - start, to: f.to - start })),
      );
    });
  }
  return just(blocks.map((block) => blockCache.get(block) ?? []));
}

// --- Clients + lexicon ----------------------------------------------------------

type HarperClient = () => Promise<void>;
const clients = new Set<HarperClient>();

/** Re-lint hook for dictionary changes. Returns an unregister function. */
export function registerHarperClient(refresh: HarperClient): () => void {
  clients.add(refresh);
  return () => clients.delete(refresh);
}

async function refreshClients(): Promise<void> {
  await Promise.all([...clients].map((refresh) => refresh()));
}

/** Warm the WASM module after the editor is up so the first pause feels snappy. */
export function warmupHarper(): void {
  void getHarper()
    .then(() => {
      if (status.state === "ready") {
        console.info(`Harper ready (${status.backend})`);
      }
    })
    .catch((error) => {
      reportHarperProblem("Harper setup failed", error);
    });
}

/**
 * Point Harper at a project dictionary + Database entity tokens.
 * Pass `databaseNames` as display names (H1s); tokens are derived here.
 */
export async function syncHarperLexicon(options: {
  rootPath: string | null;
  databaseNames?: string[];
}): Promise<void> {
  lexicon.rootPath = options.rootPath;
  lexicon.userWords = await loadUserDictionary(toMaybe(options.rootPath));
  lexicon.databaseWords = databaseWordsFromNames(options.databaseNames ?? []);
  lexicon.appliedKey = "";
  try {
    const harper = await getHarper();
    await withTimeout(applyLexicon(harper), SETUP_TIMEOUT_MS, "Harper lexicon");
    await refreshClients();
  } catch (error) {
    reportHarperProblem("Harper lexicon sync failed", error);
  }
}

/** Persist a word and re-lint every editor so the underline clears. */
export async function addHarperDictionaryWord(word: string): Promise<void> {
  const trimmed = word.trim();
  if (trimmed.length === 0) return;
  try {
    lexicon.userWords = await addUserDictionaryWord(
      toMaybe(lexicon.rootPath),
      trimmed,
    );
    lexicon.appliedKey = "";
    const harper = await getHarper();
    await applyLexicon(harper);
    await refreshClients();
  } catch (error) {
    console.error("Failed to add dictionary word", error);
  }
}
